import {Deferred} from "@core-ui/utility";
import {personalizationAdapter} from "../adapters/personalization-adapter";
import {PersonalizationContextData} from "../types";

const uuidv4 = require("random-uuid-v4");

const PERSONALIZATION_URL = "/resources/personalization/v1/";
/* eslint-disable-next-line */
const GAP_SITES_REGEX = new RegExp(
  "^https?://[^/]*(gap.com|gap.eu|gapcanada.ca|bananarepublic.ca|oldnavy.ca|thegap.co.uk|gap.co.uk|bananarepublic.co.uk|bananarepublic.com|oldnavy.com|piperlime.com|athleta.com|local)",
  "i"
);
const HTTP_REGEX = new RegExp("^https?://", "i");
const getUnknownShopperId = () => {
  let unknownShopperId: string | undefined = "";

  try {
    unknownShopperId = document?.cookie
      ?.split(";")
      ?.find((cookie) => cookie.includes("unknownShopperId"))
      ?.split("=")[1]
      ?.replace(/\|/g, "");
  } catch (err) {
    unknownShopperId = "initialize";
  }
  return unknownShopperId || "initialize";
};
const charToReplace = /[!'#()*]/g;
export const getUrlParams = (searchQuery: string) =>
  searchQuery
    .substr(1)
    .split("&")
    .reduce((params: {[key: string]: string}, val) => {
      let [name, value] = (val || "").split("=");
      name = name.replace(charToReplace, "");
      value = (value || "").replace(charToReplace, "");
      if (name && value && !params[name]) {
        // eslint-disable-next-line no-param-reassign
        params[name] = encodeURIComponent(value);
      }
      return params;
    }, {});

export const urlParamList = [
  "ap",
  "ev",
  "pid",
  "cid",
  "pcid",
  "ffpc",
  "rpqa",
  "tid",
];

export const buildUrlParams = (
  queryParams: {[x: string]: string},
  hashParams: {[x: string]: string},
  urlParamList: any[]
) => {
  const queryKeys = Object.keys(queryParams);
  const hashKeys = Object.keys(hashParams);
  return urlParamList
    .map((param) => {
      const queryKey = queryKeys.find((key) => key.toLowerCase() === param);
      const hashKey = hashKeys.find((key) => key.toLowerCase() === param);

      const queryValue = queryParams[queryKey as string] ?? "";
      const hashValue = hashParams[hashKey as string] ?? "";

      let paramKey;
      if (!!queryKey && !!queryValue) {
        paramKey = queryKey;
      } else if (!!hashValue && !!hashKey) {
        paramKey = hashKey;
      } else {
        paramKey = "";
      }
      const value = queryValue || hashValue;
      return value ? `&${paramKey}=${encodeURIComponent(value)}` : "";
    })
    .reduce((acc, param) => acc + param, "");
};

const getParam = (key: string, val = "") =>
  val?.length ? `&${key}=${encodeURIComponent(val)}` : "";

// eslint-disable-next-line max-len
export const getReferrer = (ref = document.referrer) =>
  GAP_SITES_REGEX.test(ref) || !HTTP_REGEX.test(ref)
    ? ""
    : `&referrer=${escape(encodeURIComponent(ref))}`;

// adapted from https://stackoverflow.com/a/46946573
export const timeout = (ms: number, promise: Promise<any>) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error("timeout"));
    }, ms);

    promise
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch((reason) => {
        clearTimeout(timer);
        reject(reason);
      });
  });

declare global {}

export const getOrigin = (originPathOverride: any) =>
  originPathOverride || ((global as any)?.location?.pathname ?? "/");

type FetchPersonalizationType = {
  marketAgnosticBrandCode: number;
  errorLogger: (error: Error) => void;
  breakpointData: {
    size: string;
  };
  originPathOverride: any;
  pageType: string;
};

export const fetchPersonalization = ({
  marketAgnosticBrandCode,
  errorLogger,
  breakpointData,
  originPathOverride,
  pageType,
}: FetchPersonalizationType) => {
  const queryParams = getUrlParams(window.location.search);
  const hashParams = getUrlParams(window.location.hash);
  const originPath = getOrigin(originPathOverride);
  const unknownShopperId = getUnknownShopperId();
  const referrer = getReferrer();
  const {size} = breakpointData;
  const {DI: mdsId} = queryParams;
  const correlationId = uuidv4();

  /*
  if this param exists it should be included to ensure PARTNER
  cookie reflects affiliate info for tracking
  */

  const urlParams = buildUrlParams(queryParams, hashParams, urlParamList);
  const channel = "WEB";
  const url = `${PERSONALIZATION_URL}${unknownShopperId}?originPath=${originPath}${getParam(
    "mdsId",
    mdsId
  )}${getParam("screensize", size)}${urlParams}${referrer}${getParam(
    "channel",
    channel
  )}${getParam("correlation-id", correlationId)}${getParam(
    "pageType",
    pageType
  )}`;

  const {personalizationCache} = window;

  if (personalizationCache) {
    return personalizationCache.promise;
  }

  window.personalizationCache = new Deferred();

  return timeout(
    7000,
    fetch(url, {
      credentials: "include",
      referrer: "origin-when-cross-origin",
    })
      .then((data) => data.json())
      .then((data) => {
        let redpointData = {};
        if (data.personalizationInfoV1) {
          redpointData = data.personalizationInfoV1
            .redpointPersonalizedMarketingData
            ? JSON.parse(
                data.personalizationInfoV1.redpointPersonalizedMarketingData
              )
            : {};
        }
        return {
          ...data,
          personalizationInfoV1: {
            ...data.personalizationInfoV1,
            redpointPersonalizedMarketingData: redpointData,
          },
        };
      })
  )
    .then(personalizationAdapter(marketAgnosticBrandCode) as any)
    .then((data) => {
      window?.personalizationCache?.resolve(
        data as unknown as PersonalizationContextData
      );
      return window?.personalizationCache?.promise;
    })
    .catch((err) => {
      const errorMessage = `[ERROR] error fetching personalization ${err}`;
      errorLogger(new Error(errorMessage));
      window?.personalizationCache?.reject(errorMessage);
    });
};
